import { useState, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import { useTranslate } from '@repo/unicity'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import InputAdornment from '@mui/material/InputAdornment'
import { UInput } from './UInput.component'
import { PickerColumn, PickerContainer } from './picker-column.component'

const DropdownIcon = () => (
    <svg width="17" height="9" viewBox="0 0 17 9" fill="none" data-testid="country-selection-box-svg">
        <path
            d="M16 1.55886L9.01234 8L7.9045 8L1 1.55629L1.56062 1.00257L8.23176 7.22737L8.68777 7.22737L15.4421 1L16 1.55886Z"
            fill="#99bcdf"
            stroke="#99bcdf"
            strokeWidth="0.3"
            data-testid="country-selection-box-path"
        />
    </svg>
)

const FlagImg = styled('img')`
    width: 24px;
    height: 18px;
    object-fit: cover;
    border-radius: 2px;
    flex-shrink: 0;
`

export interface CountryItem {
    country_code_2: string
    name_english: string
    name_native?: string
    flags_url?: string
}

export interface CountrySelectionBoxProps {
    countryList: CountryItem[]
    value: string
    onChange: (code: string) => void
    label?: string
    disabled?: boolean
    error?: boolean
    helperText?: string
    getDisplayName?: (item: CountryItem) => string
    inputTestId?: string
}

const defaultGetDisplayName = (item: CountryItem) => item.name_english

export const CountrySelectionBox = ({
    countryList,
    value,
    onChange,
    label = 'Country',
    disabled,
    error,
    helperText,
    getDisplayName = defaultGetDisplayName,
    inputTestId
}: CountrySelectionBoxProps) => {
    const { translate } = useTranslate()
    const [open, setOpen] = useState(false)
    const [wheelValue, setWheelValue] = useState('')

    const options = countryList.map(getDisplayName)

    const selectedCountry = countryList.find(c => c.country_code_2 === value)
    const displayValue = selectedCountry ? getDisplayName(selectedCountry) : ''

    useEffect(() => {
        if (open) setWheelValue(displayValue)
    }, [open, displayValue])

    const handleOpen = () => {
        if (!disabled) setOpen(true)
    }

    const handleConfirm = () => {
        const found = countryList.find(c => getDisplayName(c) === wheelValue)
        if (found) onChange(found.country_code_2)
        setOpen(false)
    }

    const handleCancel = () => setOpen(false)

    return (
        <Box sx={{ width: '100%', flex: 1, minWidth: 0 }} data-testid="country-selection-box-box-uinput-handle-open">
            <UInput
                label={label}
                value={displayValue}
                onClick={handleOpen}
                disabled={disabled}
                error={error}
                helperText={helperText}
                fullWidth
                slotProps={{
                    htmlInput: {
                        readOnly: true,
                        ...(inputTestId ? { 'data-testid': inputTestId } : {})
                    },
                    input: {
                        readOnly: true,
                        startAdornment: selectedCountry?.flags_url ? (
                            <InputAdornment position="start" data-testid="country-selection-box-input-adornment-1">
                                <FlagImg src={selectedCountry.flags_url} alt="" data-testid="country-selection-box-flag-img" />
                            </InputAdornment>
                        ) : undefined,
                        endAdornment: (
                            <InputAdornment position="end" sx={{ pointerEvents: 'none' }} data-testid="country-selection-box-input-adornment-2">
                                <DropdownIcon data-testid="country-selection-box-dropdown-icon" />
                            </InputAdornment>
                        )
                    }
                }}
                sx={{
                    cursor: disabled ? 'default' : 'pointer',
                    '& .MuiInputBase-input': {
                        cursor: disabled ? 'default' : 'pointer',
                        caretColor: 'transparent',
                        ...(selectedCountry?.flags_url && { paddingLeft: '8px' })
                    }
                }}
                data-testid="country-selection-box-uinput-handle-open"
            />
            <Dialog
                open={open}
                onClose={handleCancel}
                PaperProps={{
                    sx: {
                        backgroundColor: '#f7f7f7',
                        width: 270,
                        borderRadius: '7px',
                        overflow: 'hidden'
                    }
                }}
                data-testid="country-selection-box-dialog">
                <DialogContent sx={{ p: 0, overflow: 'hidden' }} data-testid="country-selection-box-dialog-content">
                    <PickerContainer data-testid="country-selection-box-picker-container">
                        <PickerColumn
                            options={options}
                            value={wheelValue}
                            onChange={setWheelValue}
                            testIdPrefix="country-selection-box"
                            data-testid="country-selection-box-picker-column-set-wheel-value"
                        />
                    </PickerContainer>
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'center', borderTop: '1px solid #e3e8ed', p: 0 }} data-testid="country-selection-box-dialog-actions">
                    <ActionBtn onClick={handleCancel} data-testid="country-selection-box-action-btn-handle-cancel">
                        {translate('cancel')}
                    </ActionBtn>
                    <ActionBtn onClick={handleConfirm} sx={{ fontWeight: 'bold' }} data-testid="country-selection-box-action-btn-handle-confirm">
                        {translate('ok')}
                    </ActionBtn>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

const ActionBtn = styled(Button)`
    flex: 1;
    font-family: 'Poppins', sans-serif;
    color: #22aaec;
    text-transform: uppercase;
    border-radius: 0;
    padding: 10px;

    &:hover {
        background: transparent;
    }
`
